import { type } from "@/api/arktype";

const sharedNodeDefs = {
	mark: { "+": "delete", type: "'bold' | 'underline' | 'italic'" },
	textNode: { "+": "delete", type: "'text'", text: "string > 0", "marks?": "mark[]" },
	heading: {
		"+": "delete",
		type: "'heading'",
		attrs: { "+": "delete", id: "string > 0", level: "2 | 3 | 4", "align?": "'center'" },
		"content?": "inline[]",
	},
	paragraph: {
		"+": "delete",
		type: "'paragraph'",
		attrs: {
			"+": "delete",
			id: "string > 0",
			"emphasis?": "'paraphrase' | 'inspirational'",
			"align?": "'center'",
		},
		"content?": "inline[]",
	},
	listItem: {
		"+": "delete",
		type: "'list-item'",
		attrs: { "+": "delete", id: "string > 0" },
		"content?": "inline[]",
	},
	step: {
		"+": "delete",
		type: "'step'",
		attrs: { "+": "delete", id: "string > 0", title: "string" },
		"content?": "inline[]",
	},
	tableCell: {
		"+": "delete",
		type: "'table-cell'",
		attrs: { "+": "delete", id: "string > 0" },
		"content?": "inline[]",
	},
	tableRow: {
		"+": "delete",
		type: "'table-row'",
		attrs: { "+": "delete", id: "string > 0" },
		content: "tableCell[]",
	},
	table: {
		"+": "delete",
		type: "'table'",
		attrs: { "+": "delete", id: "string > 0" },
		content: "tableRow[]",
	},
	cardChild: "heading | paragraph | listItem | step | table",
	variantCard: {
		"+": "delete",
		type: "'variant-card'",
		attrs: {
			"+": "delete",
			id: "string > 0",
			variant: "'welcome' | 'info' | 'alert'",
		},
		content: "cardChild[]",
	},
	pageBreak: { "+": "delete", type: "'page-break'", attrs: { "+": "delete", id: "string > 0" } },
	signatureSlot: {
		"+": "delete",
		type: "'signature-slot'",
		attrs: { "+": "delete", id: "string > 0" },
		"content?": "inline[]",
	},
	signatureRow: {
		"+": "delete",
		type: "'signature-row'",
		attrs: { "+": "delete", id: "string > 0" },
		content: "1 <= signatureSlot[] <= 2",
	},
	signature: {
		"+": "delete",
		type: "'signature'",
		attrs: { "+": "delete", id: "string > 0" },
		content: "signatureRow[] >= 1",
	},
	block:
		"heading | paragraph | listItem | step | table | variantCard | pageBreak | signature | image | pattern",
	page: {
		"+": "delete",
		type: "'page'",
		attrs: { "+": "delete", id: "string > 0" },
		content: "block[]",
	},
} as const;

const nodes = type
	.scope({
		...sharedNodeDefs,
		fieldNode: {
			"+": "delete",
			type: "'field'",
			attrs: { "+": "delete", fieldId: "number.integer > 0" },
		},
		image: {
			"+": "delete",
			type: "'image'",
			attrs: {
				"+": "delete",
				id: "string > 0",
				file: "string > 0",
				width: "number > 0",
				height: "number > 0",
				"align?": "'center' | 'right'",
			},
		},
		patternChild: "heading | paragraph | listItem | step | table | variantCard | signature | image",
		pattern: {
			"+": "delete",
			type: "'pattern'",
			attrs: { "+": "delete", id: "string > 0", patternId: "number.integer > 0" },
			content: "patternChild[] >= 1",
		},
		inline: "textNode | fieldNode",
	})
	.export();

export type DocumentBlock = typeof nodes.block.infer;
export type DocumentPage = typeof nodes.page.infer;
export type DocumentRoot = DocumentBlock | DocumentPage;
export type InlineNode = typeof nodes.inline.infer;
export type TreeNode =
	| DocumentBlock
	| DocumentPage
	| typeof nodes.tableRow.infer
	| typeof nodes.tableCell.infer
	| typeof nodes.signatureRow.infer
	| typeof nodes.signatureSlot.infer;

const CONTAINER_NODE_TYPE_LIST = [
	"page",
	"table",
	"table-row",
	"variant-card",
	"signature",
	"signature-row",
	"pattern",
] as const;

const CONTAINER_NODE_TYPES: ReadonlySet<string> = new Set(CONTAINER_NODE_TYPE_LIST);

type ContainerNode = Extract<TreeNode, { type: (typeof CONTAINER_NODE_TYPE_LIST)[number] }>;

function isContainerNode(node: TreeNode): node is ContainerNode {
	return (CONTAINER_NODE_TYPES as ReadonlySet<TreeNode["type"]>).has(node.type);
}

export function childrenOf(node: TreeNode): TreeNode[] | null {
	if (isContainerNode(node)) {
		return node.content;
	}

	return null;
}

function collectIds(node: TreeNode, ids: string[]) {
	ids.push(node.attrs.id);

	const children = childrenOf(node);

	if (children !== null) {
		for (const child of children) {
			collectIds(child, ids);
		}
	}
}

const UNIQUE_NODE_IDS = "a document with unique node ids";

function uniqueNodeIds(blocks: TreeNode[]) {
	const ids: string[] = [];

	for (const block of blocks) {
		collectIds(block, ids);
	}

	return new Set(ids).size === ids.length;
}

export const documentBlocks = nodes.block
	.array()
	.narrow((blocks, ctx) => uniqueNodeIds(blocks) || ctx.mustBe(UNIQUE_NODE_IDS));

export const documentPages = nodes.page
	.array()
	.atLeastLength(1)
	.narrow((pages, ctx) => uniqueNodeIds(pages) || ctx.mustBe(UNIQUE_NODE_IDS));

export function pageBlocks(pages: DocumentPage[]) {
	return pages.flatMap((page) => page.content);
}

export function composePages(
	blocks: DocumentBlock[],
	groups: string[][],
	pageIds: string[],
): DocumentPage[] {
	const pageOf = new Map<string, number>();

	for (const [index, ids] of groups.entries()) {
		for (const id of ids) {
			pageOf.set(id, index);
		}
	}

	const grouped: DocumentBlock[][] = pageIds.map(() => []);

	if (grouped.length === 0) {
		throw new Error("compose_pages_without_page_ids");
	}

	let current = 0;

	for (const block of blocks) {
		const index = pageOf.get(block.attrs.id);

		if (index !== undefined && index < grouped.length) {
			current = index;
		}

		grouped[current].push(block);
	}

	return grouped.map((content, index) => ({
		type: "page",
		attrs: { id: pageIds[index] },
		content,
	}));
}

export function collectImageFiles(blocks: DocumentBlock[]) {
	const files = new Set<string>();

	function walk(node: TreeNode) {
		if (node.type === "image") {
			files.add(node.attrs.file);

			return;
		}

		const children = childrenOf(node);

		if (children !== null) {
			for (const child of children) {
				walk(child);
			}
		}
	}

	for (const block of blocks) {
		walk(block);
	}

	return files;
}

export function mapInlines<T extends DocumentRoot>(
	blocks: T[],
	fn: (node: TreeNode, inline: InlineNode) => InlineNode | null,
): T[] {
	function walk(node: TreeNode): TreeNode {
		const children = childrenOf(node);

		if (children !== null) {
			return { ...node, content: children.map(walk) } as TreeNode;
		}

		if (node.type === "page-break" || node.type === "image") {
			return node;
		}

		const content = node.content as InlineNode[] | undefined;

		return {
			...node,
			content: content?.flatMap((inline) => {
				const mapped = fn(node, inline);

				return mapped === null ? [] : [mapped];
			}),
		} as TreeNode;
	}

	return blocks.map(walk) as T[];
}
